import { FechaCR } from '@/components/dominio';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import {
  useAprobarSolicitudBc,
  useRechazarSolicitudBc,
  useSolicitudesBc,
} from '@/hooks/usePedidosBcSolicitudes';
import type { SolicitudPedidoBC } from '@adelante/shared';
import { Check, ExternalLink, X } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

/**
 * `/admin/pedidos-bc` — solicitudes de envío a BC pendientes de autorización.
 *
 * Quien aprueba/rechaza: Admin + Ingeniería (ROLES_CONFIG en el backend, ver
 * `pedidos-bc-solicitudes.ts`). En el frontend gateamos el link del header
 * con `puedeConfigurar()`, mismo patrón que Umbrales/Densidades.
 *
 * Solicitudes creadas desde el modal "Solicitar envío a BC" del detalle de
 * colada — ver `ModalCrearPedidoBC` y `pedido-ensamblado-bc-dominio.ts`.
 *
 * Click en una fila abre el detalle (descripción, actividad…). Se pueden
 * marcar varias y aprobarlas en lote: se envían una por una porque cada
 * aprobación dispara un envío real a BC.
 */

export function PedidosBcSolicitudesPantalla() {
  const consulta = useSolicitudesBc('pendiente');
  const aprobar = useAprobarSolicitudBc();
  const rechazar = useRechazarSolicitudBc();

  const solicitudes = consulta.data?.data?.solicitudes ?? [];
  const errorBackend = consulta.data?.error?.mensaje ?? null;

  const [rechazarInfo, setRechazarInfo] = useState<SolicitudPedidoBC | null>(null);
  const [detalle, setDetalle] = useState<SolicitudPedidoBC | null>(null);
  const [seleccion, setSeleccion] = useState<Set<number>>(new Set());
  const [progreso, setProgreso] = useState<{ hecho: number; total: number } | null>(null);

  const ocupado = rechazar.isPending || aprobar.isPending || progreso !== null;
  const seleccionadas = solicitudes.filter((s) => seleccion.has(s.id));
  const todasMarcadas = solicitudes.length > 0 && seleccionadas.length === solicitudes.length;

  const alternar = (id: number) =>
    setSeleccion((prev) => {
      const sig = new Set(prev);
      if (sig.has(id)) sig.delete(id);
      else sig.add(id);
      return sig;
    });

  const alternarTodas = () =>
    setSeleccion(todasMarcadas ? new Set() : new Set(solicitudes.map((s) => s.id)));

  const aprobarSeleccionadas = async () => {
    const lote = seleccionadas;
    if (lote.length === 0) return;
    setProgreso({ hecho: 0, total: lote.length });
    let fallidas = 0;
    for (const [i, s] of lote.entries()) {
      try {
        const r = await aprobar.mutateAsync({ id: s.id });
        if (!r.data) fallidas++;
      } catch {
        fallidas++;
      }
      setProgreso({ hecho: i + 1, total: lote.length });
    }
    setProgreso(null);
    setSeleccion(new Set());
    if (fallidas > 0) {
      toast.warning(`${fallidas} de ${lote.length} solicitudes no se pudieron aprobar`);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Envíos a BC por autorizar</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Coladas con una solicitud de envío a Business Central pendiente. Aprobar dispara el envío
          real a BC en ese momento; rechazar deja la colada libre para que el operador corrija la
          obra y vuelva a solicitar. Click en una fila para ver el detalle.
        </p>
      </div>

      {errorBackend && (
        <Card className="border-destructive/40 bg-destructive/5">
          <CardContent className="p-4 text-sm text-destructive">{errorBackend}</CardContent>
        </Card>
      )}

      {consulta.isLoading && (
        <Card>
          <CardContent className="space-y-2 p-4">
            <Skeleton className="h-6 w-1/2" />
            <Skeleton className="h-6 w-1/3" />
            <Skeleton className="h-6 w-2/3" />
          </CardContent>
        </Card>
      )}

      {!consulta.isLoading && solicitudes.length === 0 && !errorBackend && (
        <Card>
          <CardContent className="p-6 text-center text-sm text-muted-foreground">
            No hay solicitudes pendientes de autorización.
          </CardContent>
        </Card>
      )}

      {seleccionadas.length > 0 && (
        <div className="flex items-center justify-between rounded-md border bg-muted/40 px-4 py-2">
          <span className="text-sm">
            {seleccionadas.length} seleccionada{seleccionadas.length === 1 ? '' : 's'} ·{' '}
            {seleccionadas.reduce((a, s) => a + s.cantidad_m3, 0).toFixed(2)} m³
          </span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={ocupado}
              onClick={() => setSeleccion(new Set())}
            >
              Limpiar
            </Button>
            <Button size="sm" className="gap-1.5" disabled={ocupado} onClick={aprobarSeleccionadas}>
              <Check className="h-3.5 w-3.5" />
              {progreso
                ? `Aprobando ${progreso.hecho}/${progreso.total}…`
                : `Aprobar ${seleccionadas.length} seleccionada${seleccionadas.length === 1 ? '' : 's'}`}
            </Button>
          </div>
        </div>
      )}

      {!consulta.isLoading && solicitudes.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <input
                      type="checkbox"
                      aria-label="Seleccionar todas"
                      checked={todasMarcadas}
                      disabled={ocupado}
                      onChange={alternarTodas}
                    />
                  </TableHead>
                  <TableHead>Colada</TableHead>
                  <TableHead>Obra</TableHead>
                  <TableHead>Almacén destino</TableHead>
                  <TableHead className="text-right">m³</TableHead>
                  <TableHead>Solicitado por</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {solicitudes.map((s) => (
                  <TableRow
                    key={s.id}
                    className="cursor-pointer"
                    data-state={seleccion.has(s.id) ? 'selected' : undefined}
                    onClick={() => setDetalle(s)}
                  >
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label={`Seleccionar colada ${s.codigo_interno_colada}`}
                        checked={seleccion.has(s.id)}
                        disabled={ocupado}
                        onChange={() => alternar(s.id)}
                      />
                    </TableCell>
                    <TableCell className="font-mono">#{s.codigo_interno_colada}</TableCell>
                    <TableCell>
                      {s.obra_works_no ? (
                        <>
                          <span className="font-mono">{s.obra_works_no}</span>
                          {s.obra_display_name && (
                            <div className="text-xs text-muted-foreground">
                              {s.obra_display_name}
                            </div>
                          )}
                        </>
                      ) : (
                        <span className="italic text-muted-foreground">— sin obra</span>
                      )}
                    </TableCell>
                    <TableCell className="font-mono">{s.codigo_almacen_destino}</TableCell>
                    <TableCell className="text-right font-mono">
                      {s.cantidad_m3.toFixed(2)}
                    </TableCell>
                    <TableCell>{s.solicitado_por_email ?? '—'}</TableCell>
                    <TableCell>
                      <FechaCR fecha={s.fecha_solicitud} formato="corto" />
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1.5"
                          disabled={ocupado}
                          onClick={() => setRechazarInfo(s)}
                        >
                          <X className="h-3.5 w-3.5" /> Rechazar
                        </Button>
                        <Button
                          size="sm"
                          className="gap-1.5"
                          disabled={ocupado}
                          onClick={() => aprobar.mutate({ id: s.id })}
                        >
                          <Check className="h-3.5 w-3.5" /> Aprobar
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <ModalDetalle
        solicitud={detalle}
        cerrar={() => setDetalle(null)}
        deshabilitado={ocupado}
        onAprobar={(s) => aprobar.mutate({ id: s.id }, { onSuccess: () => setDetalle(null) })}
        onRechazar={(s) => {
          setDetalle(null);
          setRechazarInfo(s);
        }}
      />

      <ModalRechazar
        solicitud={rechazarInfo}
        cerrar={() => setRechazarInfo(null)}
        onConfirmar={(motivo) =>
          rechazarInfo &&
          rechazar.mutate(
            { id: rechazarInfo.id, motivo },
            { onSuccess: () => setRechazarInfo(null) },
          )
        }
        cargando={rechazar.isPending}
      />
    </div>
  );
}

function Dato(props: { titulo: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{props.titulo}</dt>
      <dd className="mt-0.5 text-sm">{props.children}</dd>
    </div>
  );
}

function ModalDetalle(props: {
  solicitud: SolicitudPedidoBC | null;
  cerrar: () => void;
  onAprobar: (s: SolicitudPedidoBC) => void;
  onRechazar: (s: SolicitudPedidoBC) => void;
  deshabilitado: boolean;
}) {
  const s = props.solicitud;
  return (
    <Dialog open={s !== null} onOpenChange={(o) => !o && props.cerrar()}>
      <DialogContent>
        {s && (
          <>
            <DialogHeader>
              <DialogTitle>Colada #{s.codigo_interno_colada}</DialogTitle>
              <DialogDescription>
                Solicitud de envío a BC por {s.solicitado_por_email ?? '—'}
              </DialogDescription>
            </DialogHeader>
            <dl className="grid grid-cols-2 gap-3">
              <Dato titulo="Obra">
                {s.obra_works_no ? (
                  <>
                    <span className="font-mono">{s.obra_works_no}</span>
                    {s.obra_display_name && ` — ${s.obra_display_name}`}
                  </>
                ) : (
                  '— sin obra'
                )}
              </Dato>
              <Dato titulo="Almacén destino">
                <span className="font-mono">{s.codigo_almacen_destino}</span>
              </Dato>
              <Dato titulo="m³">
                <span className="font-mono">{s.cantidad_m3.toFixed(2)}</span>
              </Dato>
              <Dato titulo="Fecha solicitud">
                <FechaCR fecha={s.fecha_solicitud} formato="corto" />
              </Dato>
              <Dato titulo="Actividad">{s.actividad_nombre ?? '—'}</Dato>
              <Dato titulo="Receta">{s.receta_blend_nombre ?? '—'}</Dato>
              <Dato titulo="Destino digitado">{s.destino_raw ?? '—'}</Dato>
              <Dato titulo="Origen">
                {s.origen_registro === 'manual' ? 'Registro manual (Fiori)' : 'Planta'}
              </Dato>
            </dl>
            <div>
              <div className="text-xs text-muted-foreground">Descripción / observaciones</div>
              <p className="mt-1 whitespace-pre-wrap rounded-md border bg-muted/30 p-3 text-sm">
                {s.observaciones?.trim() || (
                  <span className="italic text-muted-foreground">Sin descripción</span>
                )}
              </p>
            </div>
            <DialogFooter className="gap-2 sm:justify-between">
              <Button variant="ghost" asChild>
                <Link to={`/coladas/${s.id_colada}`} className="gap-1.5">
                  <ExternalLink className="h-4 w-4" /> Ver colada completa
                </Link>
              </Button>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  disabled={props.deshabilitado}
                  onClick={() => props.onRechazar(s)}
                >
                  <X className="h-4 w-4" /> Rechazar
                </Button>
                <Button disabled={props.deshabilitado} onClick={() => props.onAprobar(s)}>
                  <Check className="h-4 w-4" /> Aprobar
                </Button>
              </div>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ModalRechazar(props: {
  solicitud: SolicitudPedidoBC | null;
  cerrar: () => void;
  onConfirmar: (motivo: string) => void;
  cargando: boolean;
}) {
  const [motivo, setMotivo] = useState('');
  const abierto = props.solicitud !== null;
  return (
    <Dialog
      open={abierto}
      onOpenChange={(o) => {
        if (!o) {
          setMotivo('');
          props.cerrar();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Rechazar solicitud — colada #{props.solicitud?.codigo_interno_colada}
          </DialogTitle>
          <DialogDescription>
            La colada queda sin pedido BC. El operador ve el motivo en el detalle de la colada y
            puede corregir la obra y volver a solicitar.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const limpio = motivo.trim();
            if (limpio) props.onConfirmar(limpio);
          }}
          className="grid gap-3"
        >
          <Label htmlFor="motivo-rechazo">Motivo del rechazo</Label>
          <Textarea
            id="motivo-rechazo"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Ej: la obra asignada no corresponde, revisar destino real del colado…"
            rows={4}
            maxLength={500}
            required
          />
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={props.cerrar}
              disabled={props.cargando}
            >
              <X className="h-4 w-4" /> Cancelar
            </Button>
            <Button type="submit" variant="destructive" disabled={props.cargando || !motivo.trim()}>
              <X className="h-4 w-4" />
              {props.cargando ? 'Rechazando…' : 'Rechazar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
